import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Col, Row} from "reactstrap";
import classnames from "classnames";
import {InputForm} from "../../base/input/input";
import {DatePickerForm} from "../../base/date-picker";
import Button from "../../base/button/button";
import Form from "../../base/form";
import {DropdownForm} from "../../base/dropdown/dropdown";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import DateUtils from "../../../../core/services/date-utils";
import {MuiRFF, Yup} from '../../form';
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";


const formKeys = {
    keyword: 'keyword',
    type: 'type',
    startDate: 'startDate',
    endDate: 'endDate',
}

const schema = Yup.object().shape({
    [formKeys.keyword]: Yup.string().nullable(),
    [formKeys.type]: Yup.object().nullable(),
    [formKeys.startDate]: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .maxDate(Yup.ref(formKeys.endDate), ValidateMessages.lessThan('End Date'))
        .disableFutureDate(ValidateMessages.maxDate("today"), 'day')
        .nullable(),
    [formKeys.endDate]: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .minDate(Yup.ref(formKeys.startDate), ValidateMessages.greaterThan('Start Date'))
        .disableFutureDate(ValidateMessages.maxDate("today"), 'day')
        .nullable(),
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);


const CreditCardTransactionSummarySearchSection = ({searchData, showType}) => {
    const [types, setTypes] = useState([]);
    const isMounted = useIsMounted();

    /**
     * Memo version of the initial values in the form
     * @type {object}
     */
    const initialValues = useMemo(() => ({
        [formKeys.startDate]: searchData.filter?.startDate ? DateUtils.serverToUTC(searchData.filter?.startDate) : undefined,
        [formKeys.endDate]: searchData.filter?.endDate ? DateUtils.serverToUTC(searchData.filter?.endDate) : undefined,
        [formKeys.keyword]: searchData.filter?.keyword,
        [formKeys.type]: types.find(e => e.code === searchData.filter?.type),
    }), [searchData.filter, types]);

    /**
     * Fetches the types of transactions from server:
     * - Calls the api regarding this action
     * - in case of success, sets the [types] in inner state with received values
     */
    const getTransactionTypes = useCallback(async () => {
        const response = await Api.getOnlinePaymentReportTransactionTypes()
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setTypes(response?.data ?? []);
        }
    }, [isMounted])

    /**
     * Listens to the changes of [showType]
     * Calls the function for getting the list of types if [types] is empty
     */
    useEffect(() => {
        if (showType) {
            getTransactionTypes().then()
        }
    }, [showType, getTransactionTypes])

    /**
     * Submits the form values and triggers search by changing the query
     * @param values
     */
    const onSubmit = (values) => {
        searchData.setFilters(prevState => ({
            ...prevState,
            ...values,
            [formKeys.startDate]: values[formKeys.startDate] ? DateUtils.UTCToServer(values[formKeys.startDate]).format(DateUtils.withoutOffsetFormat) : undefined,
            [formKeys.endDate]: values[formKeys.endDate] ? DateUtils.UTCToServer(values[formKeys.endDate]).format(DateUtils.withoutOffsetFormat) : undefined,
        }));
    }

    return (
        <>
            <Form
                onSubmit={onSubmit}
                validate={validate}
                initialValues={initialValues}
                render={({submitting, values, form}) => {
                    return (
                        <Row className='align-items-center justify-content-center'>
                            <Col xs={12} className={classnames('px-4 mb-3')}>
                                <InputForm
                                    onChange={(val) => form.change(formKeys.keyword, val)}
                                    label={'Keyword'}
                                    placeholder={"Reference Number, Auth Code, Description..."}
                                    name={formKeys.keyword}
                                    type={'text'}
                                    required={required[formKeys.keyword]}
                                    value={values[formKeys.keyword] ?? ''}
                                />
                            </Col>
                            {
                                showType &&
                                <Col xs={12} className={'px-4 mb-3'}>
                                    <DropdownForm
                                        label={'Transaction Type'}
                                        name={formKeys.type}
                                        required={false}
                                        data={types.map(e => ({
                                            value: e,
                                            label: e.title,
                                            id: e.code,
                                        })) ?? []}
                                    />
                                </Col>
                            }
                            <Col xs={12} sm={6} lg={6} className={'px-4 pl-sm-4 pr-sm-1 mb-3'}>
                                <DatePickerForm
                                    name={formKeys.startDate}
                                    label={'Start Date'}
                                    required={required[formKeys.startDate]}
                                    maxDate={values[formKeys.endDate]}
                                    disableFuture
                                />
                            </Col>
                            <Col xs={12} sm={6} lg={6} className={'px-4 pr-sm-4 pl-sm-1 mb-3'}>
                                <DatePickerForm
                                    name={formKeys.endDate}
                                    label={'End Date'}
                                    required={required[formKeys.endDate]}
                                    minDate={values[formKeys.startDate]}
                                    disableFuture
                                />
                            </Col>
                            <Col xs={12}
                                 className='px-3 mt-2 align-items-center row no-gutters justify-content-center align-items-center'>
                                <Button
                                    disabled={!!submitting || searchData.loading}
                                    type={'submit'}
                                    variant={'contained'}
                                    color={'primary'}
                                    className='py-3 px-8'
                                >
                                    {
                                        submitting
                                            ? "Searching..."
                                            : "Search"
                                    }
                                </Button>
                            </Col>
                        </Row>
                    );
                }}
            />
        </>
    );
}

export default CreditCardTransactionSummarySearchSection;
